import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import config from '../config';
import { useCopilot } from '../contexts/CopilotContext';

interface ReminderEmail {
  id: string;
  reminder_id: string;
  certificate_id: string;
  recipient_email: string | null;
  email_subject: string;
  email_body: string;
  status: string;
  scheduled_send_date: string | null;
  audience?: string;
  tenant_name?: string | null;
  provider_name?: string | null;
}

interface ReminderEmailDialogProps {
  open: boolean;
  reminderId: string;
  reminder?: {
    id: string;
    reminder_date: string;
    [key: string]: any;
  };
  // Which of the reminder's emails (provider or tenant) to load into the editor first —
  // defaults to the first one returned (historically always the provider email) when omitted.
  initialEmailId?: string;
  onClose: () => void;
  onSave?: () => void;
}

const ReminderEmailDialog: React.FC<ReminderEmailDialogProps> = ({
  open,
  reminderId,
  reminder,
  initialEmailId,
  onClose,
  onSave
}) => {
  const { showMessage } = useCopilot();
  const [emails, setEmails] = useState<ReminderEmail[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Edit state for the first email (simplified - editing one at a time)
  const [editingEmail, setEditingEmail] = useState<ReminderEmail | null>(null);
  const [editSubject, setEditSubject] = useState('');
  const [editBody, setEditBody] = useState('');
  const [editRecipient, setEditRecipient] = useState('');

  useEffect(() => {
    if (open && reminderId) {
      loadEmails();
    }
  }, [open, reminderId]);

  const loadEmails = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${config.apiUrl}/api/reminder-emails/${reminderId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success && data.emails.length > 0) {
        setEmails(data.emails);
        // Load the requested email (provider or tenant) for editing, falling back to the first
        const targetEmail = (initialEmailId && data.emails.find((e: ReminderEmail) => e.id === initialEmailId)) || data.emails[0];
        setEditingEmail(targetEmail);
        setEditSubject(targetEmail.email_subject);
        setEditBody(targetEmail.email_body);
        setEditRecipient(targetEmail.recipient_email || '');

        // Provider email is baked into the draft at creation time — for a still-unsent draft,
        // re-sync it against the currently-registered provider so editing the provider's email
        // on /providers shows up here too, the same way the name already does elsewhere. A
        // draft that's already been sent stays as-is — that's a real historical record.
        if (targetEmail.audience === 'provider' && targetEmail.status === 'draft') {
          try {
            const providerStatusResponse = await fetch(`${config.apiUrl}/api/reminders/${reminderId}/provider-status`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (providerStatusResponse.ok) {
              const providerStatusData = await providerStatusResponse.json();
              if (providerStatusData.success && providerStatusData.found && providerStatusData.provider?.provider_email) {
                setEditRecipient(providerStatusData.provider.provider_email);
                // The body's greeting line ("Dear {provider_name},") is generated with the
                // provider's name baked in at creation time — swap in the live name here too,
                // same rationale as the recipient email above.
                const liveName = providerStatusData.provider.provider_name;
                if (liveName) {
                  setEditBody(prevBody => prevBody.replace(/^Dear [^,\n]+,/, `Dear ${liveName},`));
                }
              }
            }
          } catch (err) {
            console.error('Error refreshing live provider email:', err);
          }
        }
      } else {
        setError('No email drafts found for this reminder');
      }
    } catch (err) {
      setError('Failed to load email drafts');
      console.error('Error loading emails:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!editingEmail) return;

    setSaving(true);
    setError(null);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${config.apiUrl}/api/reminder-emails/${editingEmail.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          email_subject: editSubject,
          email_body: editBody,
          recipient_email: editRecipient
        })
      });

      const data = await response.json();
      if (data.success) {
        onSave?.();
        onClose();
      } else {
        showMessage({ text: data.error || 'Failed to save email. Please try again.' });
      }
    } catch (err) {
      showMessage({ text: 'Failed to save email. Please try again.' });
      console.error('Error saving email:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = () => {
    if (!editingEmail) return;
    showMessage({
      text: 'Delete this email draft? This cannot be undone.',
      confirm: {
        label: 'Yes, delete',
        onConfirm: async () => {
          setSaving(true);
          try {
            const token = localStorage.getItem('auth_token');
            const response = await fetch(`${config.apiUrl}/api/reminder-emails/${editingEmail.id}`, {
              method: 'DELETE',
              headers: { 'Authorization': `Bearer ${token}` },
            });
            const data = await response.json();
            if (data.success) {
              onSave?.();
              onClose();
            } else {
              showMessage({ text: data.error || 'Failed to delete email draft.' });
            }
          } catch (err) {
            showMessage({ text: 'Failed to delete email draft. Please try again.' });
            console.error('Error deleting email:', err);
          } finally {
            setSaving(false);
          }
        },
      },
    });
  };

  const handleSendNow = () => {
    if (!editingEmail) return;
    const alreadySent = editingEmail.status === 'sent';
    showMessage({
      text: alreadySent
        ? `This email was already sent to ${editRecipient}. Send it again?`
        : `Send this email to ${editRecipient} right now?`,
      confirm: {
        label: alreadySent ? 'Yes, send again' : 'Yes, send now',
        onConfirm: async () => {
          setSending(true);
          try {
            const token = localStorage.getItem('auth_token');
            const response = await fetch(`${config.apiUrl}/api/reminder-emails/${editingEmail.id}/send-now`, {
              method: 'POST',
              headers: { 'Authorization': `Bearer ${token}` },
            });
            const data = await response.json();
            if (data.success) {
              showMessage({ text: 'Email sent!' });
              onSave?.();
              onClose();
            } else {
              showMessage({ text: data.error || 'Failed to send email.' });
            }
          } catch (err) {
            showMessage({ text: 'Failed to send email. Please try again.' });
            console.error('Error sending email:', err);
          } finally {
            setSending(false);
          }
        },
      },
    });
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Edit Email Draft</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : editingEmail ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Recipient Email"
              value={editRecipient}
              onChange={(e) => setEditRecipient(e.target.value)}
              placeholder="provider@example.com"
              helperText={!editRecipient ? "⚠️ Email will not be sent without a recipient" : ""}
              error={!editRecipient}
            />

            <TextField
              fullWidth
              label="Subject"
              value={editSubject}
              onChange={(e) => setEditSubject(e.target.value)}
            />

            <TextField
              fullWidth
              label="Email Body"
              value={editBody}
              onChange={(e) => setEditBody(e.target.value)}
              multiline
              rows={10}
            />

            {editingEmail.scheduled_send_date && reminder && (
              <Typography variant="caption" color="text.secondary">
                Scheduled to send on: {new Date(reminder.reminder_date).toLocaleDateString('en-GB', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric'
                })} at {new Date(reminder.reminder_date).toLocaleTimeString('en-US', {
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false
                })}
              </Typography>
            )}
          </Box>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleDelete} color="error" disabled={saving || !editingEmail}>
          Delete Draft
        </Button>
        <Box flex={1} />
        {reminder?.copilot_paused ? (
          <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', fontWeight: 600, px: 1 }}>
            This flow is currently paused, No emails will be sent on {editingEmail?.scheduled_send_date ? new Date(editingEmail.scheduled_send_date).toLocaleDateString() : 'the scheduled date'}
          </Typography>
        ) : (
          <Button
            onClick={handleSendNow}
            variant="outlined"
            color="success"
            disabled={sending || saving || !editingEmail || !editRecipient}
            startIcon={sending ? <CircularProgress size={16} /> : null}
          >
            {sending ? 'Sending...' : editingEmail?.status === 'sent' ? 'Send Again' : 'Send Now'}
          </Button>
        )}
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving || sending || !editingEmail}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReminderEmailDialog;
